import { setGlobalFilter } from '@va/dashboard/actions/ui';
import { getWebsite } from '@va/dashboard/selectors/core';
import { getGlobalFilter } from '@va/dashboard/selectors/ui';
import { useCurrentPeriodFilter, useFreePlanStatus } from '@va/dashboard/util-hooks';
import {
  fromDayjsToMoment,
  getShortcutDateRange,
  inWebsiteTimezone,
  SessionStorage,
  updateSelectedPeriod,
} from '@va/util/helpers';
import { StorageKeysManager } from '@va/util/misc';
import moment from 'moment';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const useRefreshDateFilter = () => {
  const dispatch = useDispatch();
  const { from, until, websiteId } = useCurrentPeriodFilter();
  const { hasActiveFreePlan } = useFreePlanStatus();
  const website = useSelector(getWebsite);
  const { dateRangeKey } = useSelector(getGlobalFilter);

  useEffect(() => {
    const remainingTime = getRemainingTimeUntilMidnight();

    const timeoutId = setTimeout(() => {
      if (dateRangeKey) {
        const { start, end } = getShortcutDateRange(
          dateRangeKey,
          inWebsiteTimezone(website.installDate * 1000),
          window.timezone,
        );
        dispatch(setGlobalFilter({ from: fromDayjsToMoment(start), until: fromDayjsToMoment(end), dateRangeKey }));
        return;
      }

      const todaysSessionDate = SessionStorage.getItem(StorageKeysManager.getTodayDateKey());
      if (!todaysSessionDate) return;

      let currentSessionMomentDate: moment.Moment | undefined = undefined;

      try {
        currentSessionMomentDate = moment(JSON.parse(todaysSessionDate)).tz(window.timezone);
      } catch (error) {
        console.error(error);
      }

      if (!currentSessionMomentDate) {
        return;
      }

      const { from: updatedFrom, until: updatedUntil } = updateSelectedPeriod(
        moment(from).tz(window.timezone),
        moment(until).tz(window.timezone),
        currentSessionMomentDate,
        websiteId,
        window.timezone,
        website.installDate,
        hasActiveFreePlan,
      );
      dispatch(setGlobalFilter({ from: updatedFrom, until: updatedUntil }));
    }, remainingTime);

    return () => clearTimeout(timeoutId);
  }, [dateRangeKey, dispatch, from, hasActiveFreePlan, until, website.installDate, websiteId]);
};

const fallbackTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function getRemainingTimeUntilMidnight() {
  const now = moment().tz(window.timezone ?? fallbackTimezone);
  const midnightMoment = moment()
    .tz(window.timezone ?? fallbackTimezone)
    .endOf('day');
  return midnightMoment.diff(now);
}
